import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startRegistration } from '@simplewebauthn/browser';
import QRCode from 'qrcode';
import { authApi } from '../auth/api';
import { generatePassword } from '../lib/password';
import {
  btnDanger,
  btnGhostSm,
  btnPrimary,
  btnPrimarySm,
  cardL1,
  fieldLabelCls,
  inputCls,
  pageContainer,
  pageHeader,
  pageTitle,
  subSectionTitle,
} from '../lib/styles';

const SECURITY_KEY = ['auth', 'security'] as const;

export function SecurityPage() {
  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <h1 className={pageTitle}>Безопасность</h1>
      </div>
      <div className="space-y-4">
        <PasswordSection />
        <TotpSection />
        <PasskeysSection />
      </div>
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
      {message}
    </div>
  );
}

function SuccessBox({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-[var(--green-border)] bg-[var(--green-bg)] px-3 py-2 text-sm text-success">
      {message}
    </div>
  );
}

// ─── Смена пароля ───

function PasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [show, setShow] = useState(false);
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setDone(true);
      setCurrent('');
      setNext('');
    },
  });

  return (
    <div className={cardL1}>
      <h3 className={`${subSectionTitle} mb-3`}>Смена пароля</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setDone(false);
          change.mutate();
        }}
        className="max-w-md space-y-3"
      >
        <label className="block">
          <span className={fieldLabelCls}>Текущий пароль</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={fieldLabelCls}>Новый пароль (минимум 8 символов)</span>
          <div className="flex items-stretch gap-2">
            <input
              type={show ? 'text' : 'password'}
              required
              minLength={8}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={`${inputCls} font-mono`}
            />
            <button type="button" onClick={() => setShow((v) => !v)} className={btnGhostSm} title={show ? 'Скрыть' : 'Показать'}>
              {show ? '🙈' : '👁'}
            </button>
            <button
              type="button"
              onClick={() => {
                setNext(generatePassword());
                setShow(true);
              }}
              className={btnGhostSm}
              title="Сгенерировать надёжный пароль"
            >
              🎲
            </button>
          </div>
        </label>
        {change.error && <ErrorBox message={(change.error as Error).message} />}
        {done && <SuccessBox message="Пароль изменён" />}
        <button type="submit" disabled={change.isPending} className={btnPrimary}>
          {change.isPending ? 'Сохранение…' : 'Сменить пароль'}
        </button>
      </form>
    </div>
  );
}

// ─── TOTP (2FA) ───

function TotpSection() {
  const qc = useQueryClient();
  const { data: overview } = useQuery({ queryKey: SECURITY_KEY, queryFn: authApi.security });

  const [setup, setSetup] = useState<{ secret: string; qrDataUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const startSetup = useMutation({
    mutationFn: async () => {
      const res = await authApi.totpSetup();
      const qrDataUrl = await QRCode.toDataURL(res.otpauthUri, { width: 220, margin: 1 });
      return { secret: res.secret, qrDataUrl };
    },
    onSuccess: (data) => setSetup(data),
  });

  const confirm = useMutation({
    mutationFn: () => authApi.totpConfirm(code),
    onSuccess: () => {
      setSetup(null);
      setCode('');
      void qc.invalidateQueries({ queryKey: SECURITY_KEY });
    },
  });

  const disable = useMutation({
    mutationFn: () => authApi.totpDisable(disablePassword),
    onSuccess: () => {
      setShowDisable(false);
      setDisablePassword('');
      void qc.invalidateQueries({ queryKey: SECURITY_KEY });
    },
  });

  const enabled = overview?.totpEnabled ?? false;

  return (
    <div className={cardL1}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className={subSectionTitle}>Двухфакторная аутентификация (TOTP)</h3>
        {enabled ? (
          <span className="rounded-full border border-[var(--green-border)] bg-[var(--green-bg)] px-2.5 py-0.5 text-[11px] font-medium text-success">
            ✓ Включена
          </span>
        ) : (
          <span className="rounded-full border border-border bg-bg px-2.5 py-0.5 text-[11px] font-medium text-text2">
            ○ Выключена
          </span>
        )}
      </div>

      {!enabled && !setup && (
        <div className="max-w-md space-y-3">
          <p className="text-sm text-text2">
            Одноразовые коды из приложения-аутентификатора (Google Authenticator, 1Password, Apple
            Пароли). После включения при входе по паролю понадобится 6-значный код.
          </p>
          {startSetup.error && <ErrorBox message={(startSetup.error as Error).message} />}
          <button onClick={() => startSetup.mutate()} disabled={startSetup.isPending} className={btnPrimary}>
            {startSetup.isPending ? 'Генерация…' : 'Включить 2FA'}
          </button>
        </div>
      )}

      {setup && (
        <div className="max-w-md space-y-3">
          <p className="text-sm text-text2">
            1. Отсканируй QR-код приложением-аутентификатором (или введи секрет вручную):
          </p>
          <img src={setup.qrDataUrl} alt="QR для аутентификатора" className="rounded-lg border border-border" />
          <code className="block break-all rounded-md border border-border bg-bg3 px-3 py-2 font-mono text-xs text-text">
            {setup.secret}
          </code>
          <p className="text-sm text-text2">2. Введи код из приложения для подтверждения:</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              confirm.mutate();
            }}
            className="flex items-stretch gap-2"
          >
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              required
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className={`${inputCls} w-36 text-center font-mono tracking-[0.3em]`}
            />
            <button type="submit" disabled={confirm.isPending} className={btnPrimarySm}>
              Подтвердить
            </button>
            <button
              type="button"
              onClick={() => {
                setSetup(null);
                setCode('');
              }}
              className={btnGhostSm}
            >
              Отмена
            </button>
          </form>
          {confirm.error && <ErrorBox message={(confirm.error as Error).message} />}
        </div>
      )}

      {enabled && (
        <div className="max-w-md space-y-3">
          <p className="text-sm text-text2">
            При входе по паролю запрашивается код из приложения. Вход по passkey код не требует.
          </p>
          {!showDisable ? (
            <button onClick={() => setShowDisable(true)} className={btnDanger}>
              Отключить 2FA
            </button>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                disable.mutate();
              }}
              className="space-y-2"
            >
              <label className="block">
                <span className={fieldLabelCls}>Пароль (подтверждение отключения)</span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className={inputCls}
                />
              </label>
              {disable.error && <ErrorBox message={(disable.error as Error).message} />}
              <div className="flex gap-2">
                <button type="submit" disabled={disable.isPending} className={btnDanger}>
                  {disable.isPending ? 'Отключение…' : 'Подтвердить отключение'}
                </button>
                <button type="button" onClick={() => setShowDisable(false)} className={btnGhostSm}>
                  Отмена
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Passkeys ───

function PasskeysSection() {
  const qc = useQueryClient();
  const { data: overview } = useQuery({ queryKey: SECURITY_KEY, queryFn: authApi.security });
  const [name, setName] = useState('');

  const register = useMutation({
    mutationFn: async () => {
      const { options, challengeId } = await authApi.passkeyRegisterOptions();
      const credential = await startRegistration({
        optionsJSON: options as unknown as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      return authApi.passkeyRegisterVerify(challengeId, credential, name.trim() || 'Passkey');
    },
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: SECURITY_KEY });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => authApi.passkeyDelete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SECURITY_KEY }),
  });

  const keys = overview?.passkeys ?? [];
  const registerError =
    register.error && (register.error as Error).name !== 'NotAllowedError'
      ? (register.error as Error).message
      : null;

  return (
    <div className={cardL1}>
      <h3 className={`${subSectionTitle} mb-3`}>Passkeys (Touch ID / Face ID)</h3>
      <div className="max-w-xl space-y-3">
        <p className="text-sm text-text2">
          Вход по отпечатку пальца или Face ID без пароля и без кода 2FA. Passkey хранится в
          Связке ключей устройства и синхронизируется через iCloud.
        </p>

        {keys.length > 0 && (
          <ul className="divide-y divide-border-subtle rounded-lg border border-border">
            {keys.map((k) => (
              <li key={k.id} className="flex items-center justify-between px-4 py-2.5">
                <div>
                  <div className="text-sm font-medium text-text">🔑 {k.name}</div>
                  <div className="text-[11px] text-text-subtle">
                    добавлен {new Date(k.createdAt).toLocaleDateString('ru')}
                    {k.lastUsedAt &&
                      ` · последний вход ${new Date(k.lastUsedAt).toLocaleString('ru')}`}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Удалить passkey «${k.name}»?`)) remove.mutate(k.id);
                  }}
                  className="text-xs font-medium text-danger underline-offset-4 hover:underline"
                >
                  Удалить
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-stretch gap-2">
          <input
            type="text"
            placeholder="Название (например «MacBook Air»)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
          />
          <button
            onClick={() => register.mutate()}
            disabled={register.isPending}
            className={`${btnPrimarySm} whitespace-nowrap`}
          >
            {register.isPending ? 'Ожидание…' : '+ Добавить passkey'}
          </button>
        </div>
        {registerError && <ErrorBox message={registerError} />}
      </div>
    </div>
  );
}
